import { useCallback, useEffect, useState } from "react";
import livechatApi from "./livechatApi";

// member = masuk whitelist autoleads (diatur admin di Kelola Sales); eligible = member + memegang brand auto-assign.
// ownOnly = role sales: backend hanya mengirim percakapan miliknya (tanpa Unassigned / milik sales lain).
const INITIAL_AVAILABILITY = { eligible: false, member: false, isSupervisor: false, ownOnly: false, acceptingLeads: false, online: false, lockedByAdmin: false, lockedByName: null };

// DEV-1234 — state & aksi pembagian lead otomatis, dipisah dari index.jsx supaya shell utama tidak
// makin membengkak (listener socket `session:assigned` tetap di index.jsx, bersama listener inbox
// lainnya yang butuh loadInbox). Semua keputusan "siapa boleh apa" datang dari backend (eligible/isSupervisor
// di GET /livechat/agent/me/availability) — FE tidak menyimpan daftar role sendiri.
export default function useAutoAssign() {
    const [availability, setAvailability] = useState(INITIAL_AVAILABILITY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [agents, setAgents] = useState([]);

    const loadAvailability = useCallback(async () => {
        try {
            const { data } = await livechatApi.getMyAvailability();
            setAvailability({ ...INITIAL_AVAILABILITY, ...(data.data || {}) });
        } catch (err) {
            console.error("Error in useAutoAssign.loadAvailability:", err);
        }
    }, []);

    useEffect(() => {
        loadAvailability();
    }, [loadAvailability]);

    // Daftar sales untuk filter per sales di inbox — cuma dimuat untuk Leader/SPV.
    useEffect(() => {
        if (!availability.isSupervisor) return;
        livechatApi
            .listAgents()
            .then(({ data }) => setAgents(data.data || []))
            .catch(() => setAgents([]));
    }, [availability.isSupervisor]);

    const setAcceptingLeads = useCallback(async (acceptingLeads) => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await livechatApi.setMyAvailability(acceptingLeads);
            setAvailability((prev) => ({ ...prev, ...(data.data || {}) }));
        } catch (err) {
            setError(err?.response?.data?.message || "Gagal mengubah status kesediaan menerima lead");
            // DEV-1238 — 403 bisa berarti autoleads baru saja dikunci admin (event socket terlewat):
            // muat ulang status supaya toggle ikut terkunci.
            if (err?.response?.status === 403) loadAvailability();
        } finally {
            setSaving(false);
        }
    }, [loadAvailability]);

    // DEV-1238 — dipanggil listener socket `agent:availability:changed` di index.jsx saat admin
    // meng-override autoleads saya (on/off + kunci), supaya toggle langsung berubah tanpa reload.
    const applyAvailability = useCallback((patch) => {
        if (!patch) return;
        setAvailability((prev) => ({ ...prev, ...patch }));
    }, []);

    return {
        availability,
        applyAvailability,
        // dipanggil saat admin memasukkan/mengeluarkan saya dari whitelist: `eligible` juga bergantung
        // pada brand yang saya pegang, jadi dihitung ulang backend.
        reloadAvailability: loadAvailability,
        saving,
        error,
        clearError: () => setError(null),
        setAcceptingLeads,
        agents,
    };
}
